import { Badge, Group, Stack, Text } from '@mantine/core';
import { TagInput } from '@/shared/components/TagInput';
import { useTags } from '@/features/tag/useTags';

interface UploadTagsSectionProps {
  defaultTags: string[];
  effectiveTags: string[];
  onTagsChange: (tags: string[]) => void;
  canAddTags: boolean;
  canEditDefaultTags: boolean;
}

export function UploadTagsSection({
  defaultTags,
  effectiveTags,
  onTagsChange,
  canAddTags,
  canEditDefaultTags,
}: UploadTagsSectionProps) {
  const { tags: allTags } = useTags();

  // User-added tags beyond the defaults (for allowAddTags mode)
  const userAddedTags = effectiveTags.filter(
    (t) => !defaultTags.some((d) => d.toLowerCase() === t.toLowerCase()),
  );

  function handleAddTagsChange(newUserTags: string[]) {
    // Combine locked default tags with user-added tags
    onTagsChange([...defaultTags, ...newUserTags]);
  }

  // Neither permission: read-only display of default tags
  if (!canAddTags && !canEditDefaultTags) {
    if (defaultTags.length === 0) return null;
    return (
      <Stack gap={4}>
        <Text size="xs" fw={500} c="dimmed">Default tags</Text>
        <Group gap={4}>
          {defaultTags.map((tag) => (
            <Badge key={tag} variant="light" size="sm">{tag}</Badge>
          ))}
        </Group>
      </Stack>
    );
  }

  // allowEditDefaultTags: full editing of default tag set
  if (canEditDefaultTags) {
    return (
      <TagInput
        label="Tags"
        description="Add or remove default tags for this upload"
        value={effectiveTags}
        onChange={onTagsChange}
        allTags={defaultTags}
        allowNewTags={false}
      />
    );
  }

  // allowAddTags: defaults are locked, can add new tags beyond them
  return (
    <Stack gap="xs">
      {defaultTags.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" fw={500} c="dimmed">Default tags</Text>
          <Group gap={4}>
            {defaultTags.map((tag) => (
              <Badge key={tag} variant="light" size="sm">{tag}</Badge>
            ))}
          </Group>
        </Stack>
      )}
      <TagInput
        label="Additional tags"
        description="Add tags beyond the defaults"
        value={userAddedTags}
        onChange={handleAddTagsChange}
        allTags={allTags.filter(
          (t) => !defaultTags.some((d) => d.toLowerCase() === t.toLowerCase()),
        )}
        allowNewTags
      />
    </Stack>
  );
}
